import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { Divider } from '~/eds/src/components/Divider/Divider';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Section } from '~/eds/src/components/Section/Section';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { AribaApiType } from '~/enums/AribaApis';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { ERROR, SUCCESS } from '~/types/toast.types';

import { API_NAMES, API_VERSIONS } from '../../constants';
import AribaFooter from '../AribaFooter';
import Realm from './Realm';

function AribaMultiAPISetup() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );

  const isTokenReauthenticate = !!activeSyncPair?.isTokenReauthenticate;
  const { provider, id } = activeSyncPair;
  const stageProperties = activeSyncPair[ManageSyncPairStageType.Realm] ?? {};
  const providerOptions = activeSyncPair.providerOptions ?? {};
  const { realm, location } = isTokenReauthenticate
    ? providerOptions
    : stageProperties;
  const { errors: realmErrors } = stageProperties ?? {};
  let sectionIndex = 1;
  const getCredentialsApi = (apiType) => {
    const tokens = providerOptions.tokens ?? [];
    for (const token of tokens) {
      if (token.tokenType === apiType) {
        return token.apiKey;
      }
    }
    return '';
  };
  const extractApiCredentials = activeSyncPair[AribaApiType.EXTRACT] ?? {
    apiKey: providerOptions.apiKey ?? '',
    oauthClientId: '',
    oauthClientSecret: '',
  };
  const discoveryApiCredentials = activeSyncPair[AribaApiType.DISCOVERY] ?? {
    apiKey: getCredentialsApi(API_NAMES[AribaApiType.DISCOVERY]),
    oauthClientId: '',
    oauthClientSecret: '',
  };
  const retrievalApiCredentials = activeSyncPair[AribaApiType.RETRIEVAL] ?? {
    apiKey: getCredentialsApi(API_NAMES[AribaApiType.RETRIEVAL]),
    oauthClientId: '',
    oauthClientSecret: '',
  };
  const authenticateErrorMessage = intl.formatMessage({
    id: 'AribaMultiAPISetup.authenticateError',
    defaultMessage:
      'Authentication error due to incorrect input value(s). Check and refill the input value(s) and try again.',
    description: 'Error message when authentication fails',
  });

  const tokenErrorMessage = intl.formatMessage({
    id: 'AribaMultiAPISetup.tokenError',
    defaultMessage: 'Check and refill the input value(s) and try again.',
    description: 'Error message when token creation fails',
  });

  const [createAribaMultiTokens, createMultiTokenResult] =
    api.endpoints.createAribaMultiTokens.useMutation();

  const [updateTokensSyncPair, updateTokensSyncPairResult] =
    api.endpoints.updateSyncPairTokens.useMutation();

  const {
    data: tokenMultiData,
    isLoading: isLoadingMulti,
    isError: isErrorMulti,
    error,
  } = createMultiTokenResult;
  const {
    data: updatedData,
    isLoading: isLoadingUpdate,
    isError: isErrorUpdate,
    error: updateError,
  } = updateTokensSyncPairResult;

  const getSectionIndex = () => {
    return sectionIndex++;
  };

  const handleBack = () => {
    dispatch(slices.integrations.actions.setActiveSyncPair(null));
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.None,
      ),
    );
  };
  const handleNext = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.FileConfig,
      ),
    );
  };

  const setExtractApiCredentials = (key, value) => {
    const updatedValues = {
      ...extractApiCredentials,
      [key]: value,
    };
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        [AribaApiType.EXTRACT]: updatedValues,
      }),
    );
  };

  const setDiscoveryApiCredentials = (key, value) => {
    const updatedValues = { ...discoveryApiCredentials, [key]: value };
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        [AribaApiType.DISCOVERY]: updatedValues,
      }),
    );
  };

  const setRetrievalApiCredentials = (key, value) => {
    const updatedValues = { ...retrievalApiCredentials, [key]: value };
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        [AribaApiType.RETRIEVAL]: updatedValues,
      }),
    );
  };

  useEffect(() => {
    if (isErrorMulti || isErrorUpdate) {
      const errors = updateError ?? error;
      showToast(ERROR, authenticateErrorMessage);
      dispatch(
        slices.integrations.actions.setActiveSyncPair({
          ...activeSyncPair,
          [ManageSyncPairStageType.Realm]: {
            ...activeSyncPair[ManageSyncPairStageType.Realm],
            errors: errors?.response?.data,
          },
        }),
      );
    }
  }, [isErrorMulti, isErrorUpdate]);

  const getError = (apiType) => {
    if (isErrorMulti | isErrorUpdate) {
      const errorResponse = updateError ?? error;
      const errorData = errorResponse?.response?.data?.[apiType];
      if (errorData && 'error' in errorData) {
        return errorData.error || tokenErrorMessage;
      }
    }
    return null;
  };

  function hasNull(target) {
    for (var member in target) {
      if (target[member] === '') return true;
    }
    return false;
  }

  const isDisabled = () => {
    if (
      realmErrors &&
      (realmErrors[API_NAMES[AribaApiType.REALM]]?.error ||
        realmErrors[API_NAMES[AribaApiType.REGION]]?.error)
    ) {
      return true;
    }

    if (!realm || !location) {
      return true;
    }

    return (
      hasNull(extractApiCredentials) ||
      hasNull(discoveryApiCredentials) ||
      hasNull(retrievalApiCredentials)
    );
  };

  const handleCreateMultiToken = useCallback(() => {
    const tokens = [
      {
        ...extractApiCredentials,
        apiType: API_NAMES[AribaApiType.EXTRACT],
        location,
        version: API_VERSIONS[AribaApiType.EXTRACT][0],
      },
      {
        ...discoveryApiCredentials,
        apiType: API_NAMES[AribaApiType.DISCOVERY],
        location,
        version: API_VERSIONS[AribaApiType.DISCOVERY][0],
      },
      {
        ...retrievalApiCredentials,
        apiType: API_NAMES[AribaApiType.RETRIEVAL],
        location,
        version: API_VERSIONS[AribaApiType.RETRIEVAL][0],
      },
    ];

    if (isTokenReauthenticate) {
      updateTokensSyncPair({ id, tokens, realm, location });
    } else {
      createAribaMultiTokens({ credentials: tokens, realm, location });
    }
  }, [
    location,
    realm,
    extractApiCredentials,
    discoveryApiCredentials,
    retrievalApiCredentials,
  ]);

  const authenticationSuccessMessage = intl.formatMessage({
    id: 'AribaMultiAPISetup.authenticationSuccess',
    defaultMessage: 'Authentication success',
    description: 'Success message when authentication is successful',
  });

  const apiKeyMessage = intl.formatMessage({
    id: 'AribaMultiAPISetup.apiKey',
    defaultMessage: 'API Key',
    description: 'Label for API Key field',
  });

  const apiKeyMessagePlaceholder = intl.formatMessage({
    id: 'AribaMultiAPISetup.apiKeyPlaceholder',
    defaultMessage: 'API Key value',
    description: 'Placeholder for API Key field',
  });

  const oauthClientIdMessage = intl.formatMessage({
    id: 'AribaMultiAPISetup.oauthClientId',
    defaultMessage: 'OAuth Client ID',
    description: 'Label for OAuth Client ID field',
  });
  const oauthClientIdMessagePlaceholder = intl.formatMessage({
    id: 'AribaMultiAPISetup.oauthClientIdPlaceholder',
    defaultMessage: 'Client ID value',
    description: 'Placeholder for OAuth Client ID field',
  });

  const oauthClientSecretMessage = intl.formatMessage({
    id: 'AribaMultiAPISetup.oauthClientSecret',
    defaultMessage: 'OAuth Client Secret',
    description: 'Label for OAuth Client Secret field',
  });
  const oauthClientSecretMessagePlaceholder = intl.formatMessage({
    id: 'AribaMultiAPISetup.oauthClientSecretPlaceholder',
    defaultMessage: 'Client Secret value',
    description: 'Placeholder for OAuth Client Secret field',
  });

  useEffect(() => {
    if (!tokenMultiData) {
      return;
    }
    showToast(SUCCESS, authenticationSuccessMessage);
    const syncPairId = tokenMultiData.syncPair;
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        syncPair: syncPairId,
      }),
    );
    handleNext();
  }, [tokenMultiData]);

  useEffect(() => {
    if (!updatedData) {
      return;
    }
    if (!isErrorUpdate) {
      showToast(SUCCESS, authenticationSuccessMessage);
      dispatch(
        slices.integrations.actions.setManageSyncPairStage(
          ManageSyncPairStageType.None,
        ),
      );
    }
  }, [updatedData]);

  return (
    <Layout align="center" direction="column" spacing={8} w="100%">
      <Layout align="center">
        <ProviderLogo provider={provider} />
      </Layout>
      <Divider />
      <Layout preset="sections" spacing={8} w="60%">
        <Section
          title={intl.formatMessage(
            {
              id: 'AribaMultiAPISetup.title',
              defaultMessage: `Ariba Realm Data {sectionIndex}`,
              description:
                'Title for Ariba Multi API Setup section. Example: sectionIndex = "1"',
            },
            { sectionIndex: getSectionIndex() },
          )}
        >
          <Realm />
        </Section>
        <Section
          title={intl.formatMessage(
            {
              id: 'AribaMultiAPISetup.extractApiTitle',
              defaultMessage: `External Approval API {sectionIndex}`,
              description:
                'Title for Operational Reporting API - Extractive section. Example: sectionIndex = "2"',
            },
            { sectionIndex: getSectionIndex() },
          )}
        >
          <Layout preset="subsections">
            <FormField
              label={apiKeyMessage}
              name="API Key"
              placeholder={apiKeyMessagePlaceholder}
              input={TextInput}
              value={extractApiCredentials['apiKey'] ?? ''}
              onChange={(value) => setExtractApiCredentials('apiKey', value)}
              error={getError(API_NAMES[AribaApiType.EXTRACT])}
            />
            <FormField
              label={oauthClientIdMessage}
              name="OAuth Client ID"
              placeholder={oauthClientIdMessagePlaceholder}
              input={TextInput}
              value={extractApiCredentials['oauthClientId'] ?? ''}
              onChange={(value) =>
                setExtractApiCredentials('oauthClientId', value)
              }
              error={getError(API_NAMES[AribaApiType.EXTRACT])}
            />
            <FormField
              label={oauthClientSecretMessage}
              name="OAuth Client Secret"
              placeholder={oauthClientSecretMessagePlaceholder}
              input={TextInput}
              value={extractApiCredentials['oauthClientSecret'] ?? ''}
              onChange={(value) =>
                setExtractApiCredentials('oauthClientSecret', value)
              }
              error={getError(API_NAMES[AribaApiType.EXTRACT])}
            />
          </Layout>
        </Section>
        <Section
          title={intl.formatMessage(
            {
              id: 'AribaMultiAPISetup.discoveryApiTitle',
              defaultMessage: `Operational Reporting API - Asynchronous {sectionIndex}`,
              description:
                'Title for operational reporting API - discovery section. Example: sectionIndex = "2"',
            },
            { sectionIndex: getSectionIndex() },
          )}
        >
          <Layout preset="subsections">
            <FormField
              label={apiKeyMessage}
              placeholder={apiKeyMessagePlaceholder}
              input={TextInput}
              value={discoveryApiCredentials['apiKey'] ?? ''}
              onChange={(value) => setDiscoveryApiCredentials('apiKey', value)}
              error={getError(API_NAMES[AribaApiType.DISCOVERY])}
            />
            <FormField
              label={oauthClientIdMessage}
              placeholder={oauthClientIdMessagePlaceholder}
              input={TextInput}
              value={discoveryApiCredentials['oauthClientId'] ?? ''}
              onChange={(value) =>
                setDiscoveryApiCredentials('oauthClientId', value)
              }
              error={getError(API_NAMES[AribaApiType.DISCOVERY])}
            />
            <FormField
              label={oauthClientSecretMessage}
              placeholder={oauthClientSecretMessagePlaceholder}
              input={TextInput}
              value={discoveryApiCredentials['oauthClientSecret'] ?? ''}
              onChange={(value) =>
                setDiscoveryApiCredentials('oauthClientSecret', value)
              }
              error={getError(API_NAMES[AribaApiType.DISCOVERY])}
            />
          </Layout>
        </Section>
        <Section
          title={intl.formatMessage(
            {
              id: 'AribaMultiAPISetup.strategicContractWorkspaceApiTitle',
              defaultMessage: `Strategic Contract Workspace API {sectionIndex}`,
              description:
                'Title for strategic contract workspace API. Example: sectionIndex = "2"',
            },
            { sectionIndex: getSectionIndex() },
          )}
        >
          <Layout preset="subsections">
            <FormField
              label={apiKeyMessage}
              placeholder={apiKeyMessagePlaceholder}
              input={TextInput}
              value={retrievalApiCredentials['apiKey'] ?? ''}
              onChange={(value) => setRetrievalApiCredentials('apiKey', value)}
              error={getError(API_NAMES[AribaApiType.RETRIEVAL])}
            />
            <FormField
              label={oauthClientIdMessage}
              placeholder={oauthClientIdMessagePlaceholder}
              input={TextInput}
              value={retrievalApiCredentials['oauthClientId'] ?? ''}
              onChange={(value) =>
                setRetrievalApiCredentials('oauthClientId', value)
              }
              error={getError(API_NAMES[AribaApiType.RETRIEVAL])}
            />
            <FormField
              label={oauthClientSecretMessage}
              placeholder={oauthClientSecretMessagePlaceholder}
              input={TextInput}
              value={retrievalApiCredentials['oauthClientSecret'] ?? ''}
              onChange={(value) =>
                setRetrievalApiCredentials('oauthClientSecret', value)
              }
              error={getError(API_NAMES[AribaApiType.RETRIEVAL])}
            />
          </Layout>
        </Section>
      </Layout>
      <AribaFooter
        secondaryAction={{
          text: intl.formatMessage({
            id: 'AribaMultiAPISetup.cancel',
            defaultMessage: 'Cancel',
            description: 'Cancel button text',
          }),
          onClick: handleBack,
        }}
        mainAction={{
          text: isTokenReauthenticate
            ? intl.formatMessage({
                id: 'AribaMultiAPISetup.reauthenticate',
                defaultMessage: 'Reauthenticate',
                description: 'Button text for re-authentication',
              })
            : intl.formatMessage({
                id: 'AribaMultiAPISetup.Authenticate',
                defaultMessage: 'Authenticate',
                description: 'Button text for authentication',
              }),
          tooltip:
            isLoadingMulti || isLoadingUpdate
              ? intl.formatMessage({
                  id: 'AribaMultiAPISetup.processingAuthentication',
                  defaultMessage:
                    'System is processing authentication request. This may take a few seconds.',
                  description:
                    'Tooltip message when authentication is in progress',
                })
              : '',
          onClick: handleCreateMultiToken,
          isLoading: isLoadingMulti || isLoadingUpdate,
          disabled: isDisabled(),
        }}
      />
    </Layout>
  );
}

export default AribaMultiAPISetup;
